/**
 * pdfIntake — the shared "take a File, hand pdf-lib something it can load"
 * step for every tool page (Merge, Rotate, Compress, Watermark, …).
 *
 * Owner-password-only PDFs open fine in PDF.js but pdf-lib refuses them, so
 * a page that only ran the bytes through pdf-lib at "Apply" time died late
 * with an opaque "… is encrypted" error. Run intake through here instead:
 * the bytes come back decrypted (losslessly, empty password, via the unlock
 * engine) or a PdfLockedError is thrown so the page can point the user at
 * Protect → Unlock right away — before any thumbnails are rendered.
 *
 * See pdfDecrypt.js for the mechanics; this module only adds the File →
 * bytes step and the shared user-facing wording.
 */
import { ensurePdfLibReadable, PdfLockedError } from './pdfDecrypt';

export { PdfLockedError };

/**
 * Read a File/Blob and return an ArrayBuffer pdf-lib can operate on.
 * Throws PdfLockedError when the document needs a real user password.
 *
 * @param {Blob} file
 * @param {{ unlock?: Function }} [deps] forwarded to ensurePdfLibReadable (tests)
 */
export async function readPdfLibBytes(file, deps) {
  return ensurePdfLibReadable(await file.arrayBuffer(), deps);
}

/**
 * The one sentence every page shows for a locked file. `action` is the
 * tool-specific tail, e.g. "rotate its pages" / "merge it".
 */
export function pdfLockedMessage(action) {
  return `This PDF is password-protected. Remove the password first (Protect → Unlock), then ${action}.`;
}

/**
 * Multi-file variant (Merge, Batch): names the files that were skipped.
 * `action` is already pluralised by the caller ("merge it" / "merge them").
 */
export function pdfLockedFilesMessage(names, action) {
  const quoted = names.map((n) => `"${n}"`);
  const list = quoted.length > 1 ? `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}` : quoted[0];
  const many = names.length > 1;
  return `${list} ${many ? 'are' : 'is'} password-protected. Remove the ${many ? 'passwords' : 'password'} first (Protect → Unlock), then ${action}.`;
}

/** Map an intake failure to the message a page should display. */
export function pdfIntakeErrorMessage(err, action) {
  if (err instanceof PdfLockedError) return pdfLockedMessage(action);
  return `Failed to load PDF: ${err?.message ?? String(err)}`;
}
